import {
  BancoDeFinanciadorSchema,
  PorReasignarSchema,
  ResultadoDeEliminarFinanciadorSchema,
  type EliminacionDeFinanciador,
  type NuevoFinanciador,
  ListaDeBancosSchema,
  SofrSchema,
  type DatosDeBanco,
  FinanciacionDeExportacionSchema,
  FinanciadorSchema,
  HistoriaDeFinanciacionSchema,
  PaginaDeFinanciacionesSchema,
  ResultadoDeCargaDeFinanciacionesSchema,
  ResultadoDeExcelDeFinanciacionesSchema,
  ResumenDeFinanciacionesSchema,
  type EdicionDeFinanciacion,
  type EdicionDeFinanciador,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {z} from 'zod';
import {pedir} from './cliente';
import {refrescarDatos} from './refrescar';

// Financiaciones de las exportaciones y cupos de los financiadores (decision 0021).
export function useResumenDeFinanciaciones() {
  return useQuery({
    queryKey: ['financiacion', 'resumen'],
    queryFn: () => pedir('seguimiento/financiaciones/resumen', ResumenDeFinanciacionesSchema),
  });
}

// Uso de cada cupo semana a semana, con el maximo y la proyeccion (9/10/2026).
export function useHistoriaDeFinanciacion() {
  return useQuery({
    queryKey: ['financiacion', 'historia'],
    queryFn: () => pedir('seguimiento/financiaciones/historia', HistoriaDeFinanciacionSchema),
  });
}

export function useFinanciaciones(parametros: URLSearchParams) {
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['financiacion', 'tabla', cadena],
    queryFn: () => pedir(`seguimiento/financiaciones${cadena ? `?${cadena}` : ''}`, PaginaDeFinanciacionesSchema),
    placeholderData: keepPreviousData,
  });
}

export function useFinanciacionDeExportacion(codigo: string) {
  return useQuery({
    queryKey: ['financiacion', 'exportacion', codigo],
    queryFn: () =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}/financiacion`, FinanciacionDeExportacionSchema),
    retry: false,
  });
}

export function useGuardarFinanciacion(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: EdicionDeFinanciacion) =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}/financiacion`, FinanciacionDeExportacionSchema, {
        method: 'PUT',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: datos => {
      cliente.setQueryData(['financiacion', 'exportacion', codigo], datos);
      void cliente.invalidateQueries({queryKey: ['financiacion', 'resumen']});
      void cliente.invalidateQueries({queryKey: ['financiacion', 'tabla']});
    },
    // Un conflicto (otra persona guardo antes) recarga los datos de ahora.
    onError: () => void cliente.invalidateQueries({queryKey: ['financiacion', 'exportacion', codigo]}),
  });
}

export function useCargarFinanciaciones() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      status,
      intercompany,
      modo,
    }: {
      status: File;
      intercompany: File | null;
      modo: 'revisar' | 'aplicar';
    }) => {
      const cuerpo = new FormData();
      cuerpo.append('status', status, status.name);
      if (intercompany) cuerpo.append('intercompany', intercompany, intercompany.name);
      cuerpo.append('modo', modo);
      return pedir('seguimiento/financiaciones/cargar', ResultadoDeCargaDeFinanciacionesSchema, {
        method: 'POST',
        body: cuerpo,
      });
    },
    onSuccess: r => {
      if (r.aplicado) void refrescarDatos(cliente);
    },
  });
}

// Excel para trabajar las financiaciones (8/10/2026): se descarga, se cambia
// y se vuelve a cargar (primero se revisa).
export const RUTA_EXCEL_DE_FINANCIACIONES = '/api/seguimiento/financiaciones/excel';

export function useCargarExcelDeFinanciaciones() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({archivo, modo}: {archivo: File; modo: 'revisar' | 'aplicar'}) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo, archivo.name);
      cuerpo.append('modo', modo);
      return pedir('seguimiento/financiaciones/excel', ResultadoDeExcelDeFinanciacionesSchema, {
        method: 'POST',
        body: cuerpo,
      });
    },
    onSuccess: r => {
      if (r.aplicado) void refrescarDatos(cliente);
    },
  });
}

export function useFinanciadores() {
  return useQuery({
    queryKey: ['financiacion', 'financiadores'],
    queryFn: () => pedir('seguimiento/financiadores', z.array(FinanciadorSchema)),
  });
}

export function useGuardarFinanciador() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({codigo, cambios}: {codigo: string; cambios: EdicionDeFinanciador}) =>
      pedir(`seguimiento/financiadores/${encodeURIComponent(codigo)}`, z.array(FinanciadorSchema), {
        method: 'PUT',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cambios),
      }),
    onSuccess: () => void cliente.invalidateQueries({queryKey: ['financiacion']}),
  });
}

// Eliminar un financiador (Daniel, 16/10/2026): revisar o eliminar, con a quien
// pasan sus exportaciones.
export function useEliminarFinanciador() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({codigo, peticion}: {codigo: string; peticion: EliminacionDeFinanciador}) =>
      pedir(`seguimiento/financiadores/${encodeURIComponent(codigo)}/eliminar`, ResultadoDeEliminarFinanciadorSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(peticion),
      }),
    onSuccess: r => {
      if (r.eliminado) {
        void cliente.invalidateQueries({queryKey: ['financiacion']});
        void cliente.invalidateQueries({queryKey: ['cashflow']});
      }
    },
  });
}

// Bancos de los financiadores (Bluequest elige uno por exportacion).
export function useBancosDeFinanciador(financiador?: string | null) {
  return useQuery({
    queryKey: ['financiacion', 'bancos', financiador ?? ''],
    queryFn: () =>
      pedir(
        `seguimiento/financiaciones/bancos${financiador ? `?financiador=${encodeURIComponent(financiador)}` : ''}`,
        ListaDeBancosSchema,
      ),
  });
}

export function useGuardarBanco() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({id, datos}: {id: number | null; datos: DatosDeBanco}) =>
      pedir(`seguimiento/financiaciones/bancos${id ? `/${id}` : ''}`, BancoDeFinanciadorSchema, {
        method: id ? 'PUT' : 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(datos),
      }),
    onSuccess: () => void cliente.invalidateQueries({queryKey: ['financiacion', 'bancos']}),
  });
}

// SOFR a 90 dias del NY Fed en una fecha (fraccion).
export const consultarSofr = (fecha: string) =>
  pedir(`seguimiento/financiaciones/sofr?fecha=${encodeURIComponent(fecha)}`, SofrSchema);

// Financiador nuevo (un banco con sus tipos de credito y su cupo).
export function useCrearFinanciador() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: NuevoFinanciador) =>
      pedir('seguimiento/financiadores', z.array(FinanciadorSchema), {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(nuevo),
      }),
    onSuccess: () => void cliente.invalidateQueries({queryKey: ['financiacion']}),
  });
}

// Financiaciones con LC o BC genericos (financiador inactivo) por pasar a su banco.
export function usePorReasignar() {
  return useQuery({
    queryKey: ['financiacion', 'por-reasignar'],
    queryFn: () => pedir('seguimiento/financiaciones/por-reasignar', PorReasignarSchema),
  });
}
